import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type SearchHit } from "@/lib/api";
import { inputClass } from "./ui";

/** §7.10 ⌘K: search contacts, parcels, deals and vendors by name, phone, APN or address. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setTerm("");
      setCursor(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const trimmed = term.trim();
  const { data, isFetching } = useQuery({
    queryKey: ["search", trimmed],
    queryFn: () => api.search(trimmed),
    enabled: open && trimmed.length >= 2,
    staleTime: 10_000,
  });

  const hits = useMemo<SearchHit[]>(() => data ?? [], [data]);

  if (!open) return null;

  function go(hit: SearchHit) {
    setOpen(false);
    navigate(hit.href);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh]"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-[var(--radius-card)] border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => Math.min(c + 1, hits.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (e.key === "Enter") {
              const hit = hits[cursor];
              if (hit) go(hit);
            }
          }}
          placeholder="Search name, phone, APN, address…"
          data-testid="palette-input"
          className={inputClass + " rounded-none border-0 border-b px-3 py-3 text-base"}
        />
        <ul className="max-h-80 overflow-y-auto" data-testid="palette-results">
          {trimmed.length < 2 && (
            <li className="px-3 py-3 text-xs text-fg-muted">Type at least two characters.</li>
          )}
          {trimmed.length >= 2 && !isFetching && hits.length === 0 && (
            <li className="px-3 py-3 text-xs text-fg-muted">No matches.</li>
          )}
          {hits.map((hit, i) => (
            <li key={`${hit.kind}-${hit.id}`}>
              <button
                type="button"
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(hit)}
                className={
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm " +
                  (i === cursor ? "bg-surface-2" : "hover:bg-surface-2")
                }
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{hit.title}</span>
                  {hit.subtitle && (
                    <span className="block truncate text-xs text-fg-muted">{hit.subtitle}</span>
                  )}
                </span>
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-fg-muted">
                  {hit.kind}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
